import { supabase } from "@/integrations/supabase/client";

const CACHE_KEY = "spotrai_post_auth_route";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export async function resolvePostAuthRoute(userId: string): Promise<"/home" | "/onboarding"> {
  // Check cache first for instant redirect
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { route, ts, uid } = JSON.parse(cached);
      if (uid === userId && Date.now() - ts < CACHE_TTL) {
        return route;
      }
    }
  } catch {}

  // Race queries against a timeout so the app never hangs
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error("timeout")), 4000)
  );

  try {
    const result = await Promise.race([
      Promise.all([
        supabase
          .from("profiles")
          .select("onboarding_completed")
          .eq("user_id", userId)
          .maybeSingle(),
        supabase
          .from("workouts")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId),
        supabase
          .from("user_goals")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId),
      ]),
      timeout,
    ]);

    const [{ data: profile }, { count: workoutCount }, { count: goalsCount }] = result;

    const hasCompletedOnboarding =
      Boolean(profile?.onboarding_completed) ||
      (workoutCount ?? 0) > 0 ||
      (goalsCount ?? 0) > 0;

    if (hasCompletedOnboarding) {
      // Fire-and-forget — don't block redirect
      supabase
        .from("profiles")
        .upsert({ user_id: userId, onboarding_completed: true }, { onConflict: "user_id" })
        .then(() => {});

      localStorage.setItem(CACHE_KEY, JSON.stringify({ route: "/home", ts: Date.now(), uid: userId }));
      return "/home";
    }

    return "/onboarding";
  } catch {
    // On timeout or error, default to home so the app isn't stuck
    return "/home";
  }
}
